<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Movimientos de caja</h1>
          <p class="view-header__subtitle">Movimientos de cajas y cuentas bancarias por sucursal y categoría.</p>
        </div>
      </div>
    </div>

    <!-- STATS -->
    <div class="row">
      <div class="col-12">
        <div class="stats-card">
          <div class="stats-card__value">{{ formatoDinamicoMoneda(total) }}</div>
          <div class="stats-card__label">Total del período</div>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <q-select v-model="branchoffice_id" :options="branchofficeOptions" placeholder="Todas las sucursales" />
          </div>
          <div class="filters-panel__field">
            <q-select v-model="fund_id" :options="fundOptions" placeholder="Todas las cajas" />
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange
              :opendir="'left'"
              :initial-start="startDate"
              :initial-end="endDate"
              @datepicked="dateCreated"
            />
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn" @click="exportCSV">
              <i class="bi bi-filetype-csv"></i>
              Exportar CSV
            </button>
            <button type="button" class="filters-panel__btn" @click="exportPDF">
              <i class="bi bi-filetype-pdf"></i>
              Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import Daterange from "../global/Daterange-component.vue";
import formatMoneyMixin from "../../mixins/formatMoneyMixin";
import formatDateMixin from "../../mixins/formatDateMixin";
import QSelect from '@/components/shared/QSelect.vue';

export default {
  components: {
    VdtnetTable,
    Daterange,
    QSelect,
  },
  mixins: [formatMoneyMixin, formatDateMixin],
  data: function () {
    return {
      branchoffice_id: null,
      fund_id: null,
      fundcategory_id: null,
      funds: [],
      startDate: null,
      endDate: null,
      total: 0,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/reports/funds",
          type: "GET",
          data: (d) => {
            d.branchoffice_id = this.branchoffice_id;
            d.fund_id = this.fund_id;
            d.fundcategory_id = this.fundcategory_id;
            d.daterange = this.daterange;
            return d;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: true,
        pageLength: 50,
        searching: true,
        searchDelay: 400,
        destroy: true,
        order: [[0, 'desc']],
        ordering: true,
        lengthChange: true,
        serverSide: true,
        autoWidth: false,
      },
      fields: {
        created_at: {
          label: "Fecha",
          searchable: true,
          render: (data) => this.formatoFechaHora(data),
        },
        notes: {
          label: "Descripción",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data || ''}</span>` : data,
        },
        fund_name: { label: "Caja" },
        fund_category: { label: "Categoría" },
        amount: {
          label: "Monto",
          searchable: true,
          align: "right",
          render: this.formatoDinamicoMoneda,
        },
      },
    };
  },
  computed: {
    ...mapState('branchoffices', ['branchoffices']),
    branchofficeOptions() {
      return this.branchoffices.map(b => ({ value: b.id, label: b.name }));
    },
    fundOptions() {
      return this.funds.map(f => ({ value: f.id, label: f.name }));
    },
    daterange() {
      if (this.startDate === null || this.endDate === null) return null;
      const startDate = this.formatoFecha2Daterange(this.startDate);
      const endDate = this.formatoFecha2Daterange(this.endDate);
      return `${startDate} - ${endDate}`;
    },
    filters() {
      return {
        branchoffice_id: this.branchoffice_id,
        fund_id: this.fund_id,
        fundcategory_id: this.fundcategory_id,
        daterange: this.daterange,
      };
    },
  },
  watch: {
    filters() {
      this.reload();
    },
  },
  mounted() {
    this.setDefaultDateRange();
    this.$store.dispatch('branchoffices/getAllBranchoffices');
    this.getFunds();
    this.getTotals();
  },
  methods: {
    reload() {
      this.getTotals();
      this.refreshTable();
    },
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    dateCreated(date) {
      this.startDate = date.startDate;
      this.endDate = date.endDate;
    },
    getFunds() {
      this.$http.get('/api/funds/listSimple').then((response) => {
        this.funds = response.data.data;
      });
    },
    getTotals() {
      this.$http.get("/api/reports/fundstotal", { params: this.filters })
        .then((response) => {
          this.total = response.data.data.total;
        })
        .catch((error) => {
          console.error('getTotals:', error);
        });
    },
    exportCSV() {
      this.$http.post("/api/reports/CSVFundMovReport", this.filters);
    },
    exportPDF() {
      this.$http.post("/api/reports/PDFFundMovReport", this.filters);
    },
    setDefaultDateRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 7);
      this.startDate = this.formatDate(startDate);
      this.endDate = this.formatDate(today);
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
